// Аномалии: подозрительные seed с объяснением фразой по-русски и кнопкой
// скопировать команду воспроизведения.
//
// ЧЕТЫРЕ ТАБЛИЦЫ, А НЕ ОДНА. Это четыре разных вопроса к данным, и
// сваленные в кучу они прячут главный: «здесь сломано» тонет среди
// «здесь редко». Порядок таблиц — по убыванию срочности.

import { useEffect, useState } from 'react';
import { useStore } from '../data/store';
import { loadFindings } from '../data/source';
import type { ServerFindings } from '../data/normalize';
import { Callout, CopyButton, OriginTag, Panel, Tile } from '../components/Ui';
import {
  ANOMALY_KIND_NOTES,
  ANOMALY_KIND_TITLES,
  FLAG_PHRASES,
  METRIC_BY_KEY,
  type Anomaly,
  type AnomalyKind,
} from '../lib/stats';
import { reproduceCommand } from '../data/source';
import { num, percent } from '../lib/format';

const ORDER: AnomalyKind[] = ['broken', 'rare', 'frequency', 'outlier'];

const TONE: Record<AnomalyKind, 'bad' | 'warn' | 'plain'> = {
  broken: 'bad',
  rare: 'warn',
  frequency: 'plain',
  outlier: 'warn',
};

export function Anomalies() {
  const store = useStore();
  const { anomalies, summary } = store;
  const [limits, setLimits] = useState<Record<string, number>>({});

  const [findings, setFindings] = useState<ServerFindings | null>(null);

  // Сервер разложил находки сам и написал к каждой фразу по-русски.
  // Когда он отвечает — показываем ЕГО разбор: у него шире набор метрик,
  // и второй источник правды тут ни к чему.
  useEffect(() => {
    const server = store.server;
    const dataset = store.dataset;
    if (server === null || dataset === null) return;
    let alive = true;
    void (async () => {
      const result = await loadFindings(server.online, dataset);
      if (alive) setFindings(result.value);
    })();
    return () => {
      alive = false;
    };
  }, [store.server, store.dataset]);

  const preset = store.presets.find((p) => p.config.name === summary.configHash) ?? null;
  const lists: Record<AnomalyKind, Anomaly[]> = {
    broken: anomalies.broken,
    rare: anomalies.rare,
    frequency: anomalies.frequency,
    outlier: anomalies.outlier,
  };

  return (
    <div className="page">
      <div className="page-head">
        <h1>Аномалии</h1>
        <p>
          Четыре разных вопроса к данным, а не один список. Сломанный прогон и редкое значение —
          разные новости, и объединять их значило бы прятать первую за второй.
          {findings === null
            ? ' Сервер не отвечает, поэтому находки посчитаны в браузере — по тем же четырём видам, но по меньшему набору величин.'
            : ' Разбор сделал сервер: у него шире набор величин, и фразы показаны дословно.'}
        </p>
        <OriginTag origin={findings !== null ? 'server' : store.runsOrigin} />
      </div>

      {findings === null && (
      <div className="tiles">
        <Tile
          label="Всего отмечено"
          value={num(anomalies.total)}
          note={`${percent(anomalies.total / Math.max(1, summary.rows))} выборки из ${num(summary.rows)}`}
        />
        {ORDER.map((kind) => (
          <Tile
            key={kind}
            label={ANOMALY_KIND_TITLES[kind]}
            value={num(lists[kind].length)}
            tone={lists[kind].length === 0 ? 'good' : TONE[kind]}
            note={
              lists[kind].length === 0
                ? 'ни одного — это хороший результат'
                : `${percent(lists[kind].length / Math.max(1, summary.rows))} прогонов`
            }
          />
        ))}
      </div>
      )}

      {findings !== null && <ServerFindingsView findings={findings} />}

      {findings === null && (
      <Callout tone="info" title="Почему для большинства величин правило 3×MAD не работает">
        <p>
          На тысяче прогонов широкое распределение имеет ровно одна величина —{' '}
          <b>площадь пола</b> (десятки различных значений). У остальных их единицы: врагов шесть,
          комнат пять, аптечек три. MAD там равен нулю, и правило «больше 3×MAD» либо молчит, либо
          объявляет аномалией отличие на единицу.
        </p>
        <p>
          Поэтому величины разделены самими данными:{' '}
          <b className="mono">
            широкие — {anomalies.wideMetrics.map((k) => METRIC_BY_KEY.get(k)?.label ?? k).join(', ') || '—'}
          </b>{' '}
          (на них работает 3×MAD и p1/p99), остальные{' '}
          <span className="mono">
            ({anomalies.tightMetrics.map((k) => METRIC_BY_KEY.get(k)?.label ?? k).join(', ')})
          </span>{' '}
          — по хвосту частотной таблицы: редким считается значение, встретившееся меньше чем в 1%
          прогонов.
        </p>
      </Callout>
      )}

      {findings === null && ORDER.map((kind) => {
        const list = lists[kind];
        const limit = limits[kind] ?? 50;
        return (
          <Panel
            key={kind}
            title={`${ANOMALY_KIND_TITLES[kind]} — ${num(list.length)}`}
            flush
            aside={
              list.length > 0 ? (
                <div className="controls">
                  {list.length > limit && (
                    <button
                      className="action"
                      onClick={() => setLimits((l) => ({ ...l, [kind]: limit + 100 }))}
                    >
                      показать ещё ({num(list.length - limit)})
                    </button>
                  )}
                  <CopyButton text={list.map((a) => a.sentence).join('\n')} label="копировать фразы" />
                </div>
              ) : undefined
            }
          >
            <div style={{ padding: '10px 14px 0', fontSize: 'var(--fs-xs)', color: 'var(--text-dim)' }}>
              {ANOMALY_KIND_NOTES[kind]}
            </div>

            {list.length === 0 ? (
              <div className="empty">
                {kind === 'broken'
                  ? `ни один из ${num(summary.rows)} прогонов не сломан`
                  : `ничего не отмечено на ${num(summary.rows)} прогонах`}
              </div>
            ) : (
              <div className="scroll" style={{ maxHeight: '46vh' }}>
                <table>
                  <thead>
                    <tr>
                      <th className="n">seed</th>
                      <th>Объяснение</th>
                      <th>Флаги</th>
                      <th className="n">врагов</th>
                      <th className="n">патронов</th>
                      <th className="n">запас</th>
                      <th className="n">комнат</th>
                      <th className="n">пол</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {list.slice(0, limit).map((anomaly) => {
                      const row = anomaly.row;
                      return (
                        <tr key={anomaly.seed}>
                          <td className="n mono">{anomaly.seed}</td>
                          <td>{anomaly.sentence}</td>
                          <td>
                            {row.flags.length === 0 && row.economy.respectsAmmoDeficit ? (
                              <span className="faint">—</span>
                            ) : (
                              <>
                                {!row.economy.respectsAmmoDeficit && (
                                  <span className="tag bad">избыток патронов</span>
                                )}{' '}
                                {row.flags.map((flag) => (
                                  <span className="tag warn" key={flag} title={FLAG_PHRASES[flag] ?? flag}>
                                    {flag}
                                  </span>
                                ))}
                              </>
                            )}
                          </td>
                          <td className="n">{num(row.population.enemies)}</td>
                          <td className="n">{num(row.population.ammoTotal)}</td>
                          <td className={row.economy.ammoMargin >= 0 ? 'n bad' : 'n'}>
                            {num(row.economy.ammoMargin)}
                          </td>
                          <td className="n">{num(row.layout.rooms)}</td>
                          <td className="n">{num(row.layout.floorCells)}</td>
                          <td>
                            <CopyButton
                              text={reproduceCommand(store.dataset ?? '', anomaly.seed, preset?.file ?? null)}
                              label="повторить"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        );
      })}

      <Panel title="Что кладёт кнопка «повторить»">
        <pre className="cmd">{reproduceCommand(store.dataset ?? 'dataset', 481, preset?.file ?? null)}</pre>
        <p className="dim" style={{ marginBottom: 0, marginTop: 8 }}>
          Один seed, один прогон, тот же конфиг. Вывод пойдёт в stdout, сводка — в stderr; добавьте{' '}
          <span className="mono">--out файл.jsonl</span>, если хотите положить строку рядом с остальными.
        </p>
      </Panel>
    </div>
  );
}

/**
 * Разбор сервера: те же четыре вида, но с его набором метрик и его
 * фразами. Фразы показываются ДОСЛОВНО — переписывать их своими словами
 * значило бы завести второй источник правды.
 */
function ServerFindingsView({ findings }: { findings: ServerFindings }) {
  return (
    <>
      <Panel
        title="Чем измерена каждая величина"
        flush
        aside={
          <span className="dim">
            инструмент выбирается по выборке, а не прибит к метрике
          </span>
        }
      >
        <div className="scroll" style={{ maxHeight: '34vh' }}>
          <table>
            <thead>
              <tr>
                <th>Величина</th>
                <th>Блок</th>
                <th className="n">различных значений</th>
                <th>Почему так</th>
              </tr>
            </thead>
            <tbody>
              {findings.classification.map((entry) => (
                <tr key={entry.metric}>
                  <td>{entry.label}</td>
                  <td>
                    <span
                      className={
                        entry.classification === 'outliers'
                          ? 'tag warn'
                          : entry.classification === 'excluded'
                            ? 'tag'
                            : 'tag good'
                      }
                    >
                      {entry.block ?? entry.classification}
                    </span>
                  </td>
                  <td className="n">{num(entry.distinct)}</td>
                  <td className="dim">{entry.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="tiles">
        <Tile
          label="Признаки CLI"
          value={num(findings.flags.reduce((s, f) => s + f.count, 0))}
          tone={findings.flags.length === 0 ? 'good' : 'bad'}
          note="физически сломанное в одном прогоне"
        />
        <Tile
          label="Редкие события"
          value={num(findings.rareEvents.length)}
          tone={findings.rareEvents.length === 0 ? 'good' : 'warn'}
          note="счётчики с нулевой медианой"
        />
        <Tile
          label="Частотные таблицы"
          value={num(findings.distributions.length)}
          note="тесные величины: показана форма целиком"
        />
        <Tile
          label="Выбросы"
          value={num(findings.outliers.total)}
          tone={findings.outliers.total > 0 ? 'warn' : 'good'}
          note={`${percent(findings.outliers.share)} прогонов · только широкие величины`}
        />
        <Tile
          label="Исключено"
          value={num(findings.excluded.length)}
          note="величин, по которым не сказано ничего"
        />
      </div>

      {findings.rareEvents.length > 0 && (
        <Panel title={`Редкие события — ${num(findings.rareEvents.length)}`}>
          {findings.rareEvents.map((event) => (
            <p key={event.metric} style={{ margin: '0 0 8px' }}>
              {event.explanation}
              {event.seeds.length > 0 && (
                <span className="faint mono"> seed: {event.seeds.slice(0, 12).join(', ')}</span>
              )}
            </p>
          ))}
        </Panel>
      )}

      {findings.distributions.length > 0 && (
        <Panel title={`Частотные таблицы — ${num(findings.distributions.length)}`}>
          {findings.distributions.map((dist) => (
            <div key={dist.metric} style={{ marginBottom: 12 }}>
              <p style={{ margin: '0 0 4px' }}>{dist.explanation}</p>
              <div className="legend">
                {dist.values.map((v) => (
                  <span key={v.value} className={v.rare ? 'warn' : 'dim'}>
                    <span className="mono">{num(v.value)}</span> — {num(v.runs)} (
                    {percent(v.share, 1)})
                  </span>
                ))}
              </div>
            </div>
          ))}
        </Panel>
      )}

      {findings.outliers.items.length > 0 && (
        <Panel
          title={`Выбросы — ${num(findings.outliers.total)}`}
          flush
          aside={
            <span className="dim">
              порог {num(findings.outliers.madFactor)}×MAD · показано{' '}
              {num(findings.outliers.returned)}
              {findings.outliers.truncated > 0 && `, скрыто ${num(findings.outliers.truncated)}`}
            </span>
          }
        >
          <div className="scroll" style={{ maxHeight: '40vh' }}>
            <table>
              <thead>
                <tr>
                  <th className="n">seed</th>
                  <th>Объяснение</th>
                  <th className="n">резкость</th>
                </tr>
              </thead>
              <tbody>
                {findings.outliers.items.map((item) => (
                  <tr key={item.seed}>
                    <td className="n mono">{item.seed}</td>
                    <td>{item.explanation}</td>
                    <td className="n faint">{num(item.severity, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {findings.excluded.length > 0 && (
        <Panel title={`Исключённые величины — ${num(findings.excluded.length)}`} flush>
          <div style={{ padding: '10px 14px 0', fontSize: 'var(--fs-xs)', color: 'var(--text-dim)' }}>
            Без этого списка «мы это не считали» и «событие не случилось ни разу» выглядели бы
            одинаково — то есть никак.
          </div>
          <table>
            <thead>
              <tr>
                <th>Величина</th>
                <th>Почему исключена</th>
              </tr>
            </thead>
            <tbody>
              {findings.excluded.map((entry) => (
                <tr key={entry.metric}>
                  <td>{entry.label}</td>
                  <td className="dim">{entry.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

    </>
  );
}
